// src/Dashboard/pages/admin/Attendance/components/AttendanceChartsPanel.jsx
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FaChartLine, FaSyncAlt } from "react-icons/fa";

export default function AttendanceChartsPanel({
  loading,
  selectedGroup,
  seasonActive,
  chartData = [],
  totals = {},
  onRefresh,
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-orange-500">
            Statistiques
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
            Saison {seasonActive || "—"} · {selectedGroup?.nom || "Groupe"}
          </h2>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Présences, absences et taux de présence sur la saison.
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
        >
          <FaSyncAlt className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <MiniStat label="Présences" value={totals.present || 0} tone="emerald" />
        <MiniStat label="Absences" value={totals.absent || 0} tone="rose" />
        <MiniStat label="Taux présence" value={`${totals.rate || 0}%`} tone="orange" />
      </div>

      {chartData.length === 0 ? (
        <div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-white text-slate-400 dark:bg-slate-900">
            <FaChartLine className="text-2xl" />
          </div>

          <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
            Aucune statistique disponible
          </h3>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Les graphiques apparaîtront dès que des appels seront enregistrés.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">
              Présences / absences par mois
            </h3>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="present"
                    name="Présents"
                    stackId="a"
                    fill="#10B981"
                  />
                  <Bar
                    dataKey="absent"
                    name="Absents"
                    stackId="a"
                    fill="#F43F5E"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">
              Taux de présence
            </h3>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    name="Taux (%)"
                    stroke="#0F172A"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, tone }) {
  const tones = {
    emerald:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200",
    rose: "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-200",
    orange:
      "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-200",
  };

  return (
    <div
      className={`rounded-3xl border border-slate-200 p-5 dark:border-slate-800 ${tones[tone]}`}
    >
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}